import { http } from '@/api/http';
import type { PageUserDto, UserDto } from '@/features/admin-users/endpoints';
import type {
  FederatedIdentityDto,
  FederatedIdentityStatusDto,
  LinkFederatedIdentityRequestDto,
} from '@/features/admin-federated-identities/endpoints';

/**
 * Raw transport for the Platform console (PTV2-1021): tenant management,
 * cross-tenant user provisioning, tenant templates, cluster grants, and the
 * async job model. All endpoints sit behind the gateway's global-plane
 * authorization; a tenant user gets a 403/404 envelope.
 *
 * Field names below mirror the backend DTOs verbatim. Per the amended API
 * contract (2026-06-11) the wire shape is snake_case
 * (`schema_name`, `schema_initialized`, `created_on`); `api.ts` maps these to
 * camelCase view models. Phase 1 does NOT include image-tag overrides or the
 * deployment-manager sub-actions (deploy/update/stop/wipe) — those are Phase 2.
 */

export type TenantState = 'active' | 'inactive';

/**
 * Overridable service names (wire values are lowercase per the API contract's
 * `OverridableServiceName` enum). PTV2-1021 Phase 2.
 */
export type OverridableServiceName = 'backend' | 'frontend' | 'indexing';

export const OVERRIDABLE_SERVICES: readonly OverridableServiceName[] = [
  'backend',
  'frontend',
  'indexing',
];

/**
 * Tenant deployment status (wire values lowercase per the contract's
 * `DeploymentStatus` enum). NULL on plain tenants (no overrides). Non-terminal
 * (drives polling): `pending | pulling | deploying | wiping`. Terminal:
 * `ready | stopped | failed_*`.
 */
export type DeploymentStatus =
  | 'pending'
  | 'pulling'
  | 'deploying'
  | 'ready'
  | 'stopped'
  | 'wiping'
  | 'failed_deploy'
  | 'failed_update'
  | 'failed_stop'
  | 'failed_wipe';

/**
 * A nested override row: which service, which image tag, and the digest the
 * image resolved to at the last successful deploy (short-hash displayed in UI).
 *
 * PTV2-1433: the backend `TenantDTO` (commons-lib-v2) serializes camelCase with
 * NO `@JsonProperty` (unlike the system/pool DTOs which annotate snake_case), and
 * its enum-valued fields carry the UPPERCASE enum names on the wire (`ACTIVE`,
 * `READY`, `BACKEND`, …). These types therefore mirror the ACTUAL wire shape; the
 * mappers in `api.ts` (`mapTenant`) lower-case the enum values to the lowercase
 * FE unions (`TenantState` / `DeploymentStatus` / `OverridableServiceName`).
 */
export interface TenantServiceOverrideDto {
  /** UPPERCASE enum name on the wire (e.g. `BACKEND`); lower-cased in `mapTenant`. */
  readonly serviceName: string;
  readonly imageTag: string;
  readonly imageDigestAtDeploy?: string | null;
}

/** Wire DTO for a tenant. camelCase; enum fields are the UPPERCASE enum names. */
export interface TenantDto {
  readonly uuid: string;
  readonly name: string;
  readonly label: string | null;
  readonly schemaName: string;
  /** UPPERCASE enum name on the wire (`ACTIVE`/`INACTIVE`); lower-cased in `mapTenant`. */
  readonly state: string;
  readonly schemaInitialized: boolean;
  readonly createdOn?: string | null;
  // ── Phase 2 override fields ──
  readonly serviceOverrides?: readonly TenantServiceOverrideDto[] | null;
  /** UPPERCASE enum name on the wire (`READY`/`FAILED_DEPLOY`/…); lower-cased in `mapTenant`. */
  readonly deploymentStatus?: string | null;
  readonly currentJobExecutionId?: number | null;
}

/** Paged envelope returned by `GET /tenants`. */
export interface TenantPageDto {
  readonly content: readonly TenantDto[];
  readonly totalElements: number;
  readonly totalPages: number;
}

/**
 * Query parameters accepted by `GET /jobs` (backend `JobController`). All are
 * optional; the backend defaults `page=0 size=20` and has no default for
 * status/start/end (returns all when omitted).
 */
export interface ListJobsParams {
  readonly start?: string;
  readonly end?: string;
  readonly status?: string;
  /** Filter to a single job type, e.g. `bulkSendQuestionnaireByEmailJob` (PTV2-1162). */
  readonly jobName?: string;
  readonly page?: number;
  readonly size?: number;
}

/**
 * Override entry in a create/update body. Wire field names are camelCase per
 * the API contract's request examples (`serviceName`/`imageTag`) — NOTE this
 * differs from the snake_case RESPONSE shape on `TenantServiceOverrideDto`.
 * RE-VERIFY at integration.
 */
export interface ServiceOverrideRequestDto {
  readonly serviceName: OverridableServiceName;
  readonly imageTag: string;
}

/**
 * Body for `POST /tenants`. `name` is immutable + validated; `uuid` is
 * server-generated when omitted. When `serviceOverrides[]` is non-empty (and
 * the deployment manager is reachable) the create call returns 202 + a job and
 * additionally launches `tenantDeployJob`.
 */
export interface CreateTenantRequestDto {
  readonly uuid?: string;
  readonly name: string;
  readonly label?: string | null;
  readonly state?: TenantState;
  readonly serviceOverrides?: readonly ServiceOverrideRequestDto[];
}

/**
 * Body for `PUT /tenants/{uuid}` — `label` + `state`, plus the override editor
 * path (`serviceOverrides[]`, per the API contract's "Edit overrides" row).
 * Sending `name` is a 400.
 */
export interface UpdateTenantRequestDto {
  readonly label?: string | null;
  readonly state?: TenantState;
  readonly serviceOverrides?: readonly ServiceOverrideRequestDto[];
}

/**
 * 202 launch envelope for the override sub-actions (deploy/update/stop) and the
 * wipe DELETE. Per the API contract these return
 * `{ jobExecutionId, tenantUuid, deploymentStatus }` — DISTINCT from the core
 * `PhenotipsJobExecutionDTO` returned by init/seed. `jobExecutionId` is a number
 * here (the deployment manager's Spring Batch execution id). RE-VERIFY at
 * integration whether the create-with-overrides 202 uses this shape or the core
 * job DTO; the create flow tolerates both (see `api.ts` `extractLaunchJobId`).
 */
export interface OverrideJobLaunchDto {
  readonly jobExecutionId: number;
  readonly tenantUuid?: string;
  readonly deploymentStatus?: DeploymentStatus;
}

/** `GET /dockerhub-credentials` — token is NEVER returned; only `tokenSet`. */
export interface DockerHubCredentialsDto {
  readonly username: string;
  readonly tokenSet: boolean;
  readonly updatedOn?: string | null;
}

/** `PUT /dockerhub-credentials` — write-only token. */
export interface PutDockerHubCredentialsRequestDto {
  readonly username: string;
  readonly token: string;
}

/** Nested in `DeploymentManagerHealthDto`. */
export interface ResourceUsageDto {
  readonly overrideMemoryBytes: number;
  readonly overrideMemoryCeilingBytes: number;
  readonly overrideTenantCount: number;
}

/**
 * `GET /deployment-manager/health` — 200 only when the DM is registered in
 * discovery (dev). The gateway returns 404 in staging/prod; the frontend treats
 * any non-200 (404/503/network) as "feature unavailable", NOT an error.
 */
export interface DeploymentManagerHealthDto {
  readonly status: string;
  readonly version?: string | null;
  readonly resourceUsage?: ResourceUsageDto | null;
}

/**
 * `GET /deployment-manager/base-versions` (PTV2-1021 Phase 4) — the base image
 * tag the cluster currently runs for each overridable service, used to pre-fill
 * the create-tenant override inputs.
 *
 * NOTE: unlike the snake_case tenant DTOs in this file, this DM endpoint is
 * **camelCase on the wire** — the keys are already `backend`/`frontend`/
 * `indexing` (single-word, so casing is moot per-key, but the envelope follows
 * the DM's camelCase convention like `DeploymentManagerHealthDto`, NOT the
 * `@JsonProperty` snake_case of `TenantDTO`). No snake_case mapping is needed.
 *
 * Each value is the base image tag string, or `null` when that service is not
 * deployed on the cluster (e.g. indexing is typically absent in dev). Same
 * DM-reachability semantics as the health endpoint: a 404/503/network error
 * means "DM unavailable" → no pre-fill (empty inputs), NOT an error state.
 */
export interface BaseVersionsDto {
  readonly backend: string | null;
  readonly frontend: string | null;
  readonly indexing: string | null;
}

/**
 * Spring Batch `BatchStatus` — the exact set serialized by the backend
 * `PhenotipsJobExecutionDTO.status` field (verified against the enum). Terminal
 * states are `COMPLETED | FAILED | STOPPED | ABANDONED`; the rest are running.
 */
export type JobStatus =
  | 'STARTING'
  | 'STARTED'
  | 'STOPPING'
  | 'STOPPED'
  | 'COMPLETING'
  | 'COMPLETED'
  | 'FAILED'
  | 'ABANDONED'
  | 'UNKNOWN';

/**
 * Spring Batch `ExitStatus`, serialized via its bean getters. `exitCode` is the
 * coarse outcome (e.g. `EXECUTING`, `COMPLETED`, `FAILED`); `exitDescription`
 * carries the human-readable failure reason when failed.
 */
export interface ExitStatusDto {
  readonly exitCode?: string | null;
  readonly exitDescription?: string | null;
}

/**
 * `PhenotipsJobExecutionDTO` — the ACTUAL wire shape from
 * `commons.dto.job.PhenotipsJobExecutionDTO`. Core sets no Jackson
 * property-naming strategy, so this DTO is **camelCase** on the wire (verified
 * against `application.yml` + the DTO source, PTV2-1021). Note: `jobId` is a
 * STRING, `status` is the `BatchStatus` enum name, failure detail lives under
 * the nested `exitStatus` object, and there is NO `steps` array on this DTO.
 */
export interface JobExecutionDto {
  readonly jobId: string;
  readonly jobName: string;
  readonly status: JobStatus;
  readonly startTime?: string | null;
  readonly endTime?: string | null;
  readonly exitStatus?: ExitStatusDto | null;
  readonly jobParameters?: Readonly<Record<string, unknown>> | null;
}

/**
 * init / seed / apply-template all return `202 Accepted` + a full
 * `PhenotipsJobExecutionDTO` (verified against `TenantController`). The launch
 * response is just a job execution, so there is no separate launch shape — the
 * caller reads `jobId` to subscribe via `useJob`.
 */
export type JobLaunchDto = JobExecutionDto;

export interface ProvisionTenantUserRequestDto {
  readonly userGuid?: string;
  readonly email: string;
  readonly name: string;
  /** Optional; omitted from the body entirely when the operator leaves it blank. */
  readonly fullName?: string;
  readonly roles: readonly string[];
}

/** Created `UserDTO` returned by user provisioning (subset the UI consumes). */
export interface ProvisionedUserDto {
  readonly userGuid?: string;
  readonly guid?: string;
  readonly email?: string;
  readonly name?: string;
  readonly fullName?: string;
  readonly roles?: readonly string[];
}

export interface ReplaceTenantUserRolesRequestDto {
  readonly roles: readonly string[];
}

/**
 * `GET /tenants/{uuid}/users` + `/users/search` (PTV2-1426): the tenant-admin reach surface returns
 * the SAME `UserDTO` wire shape the global `/users` directory does (`admin-users/endpoints.ts`), just
 * scoped to one tenant's schema server-side. Re-using that type here avoids a duplicate hand-rolled
 * copy of a DTO that already drifted from the OpenAPI contract once.
 */
export type TenantAdminUserDto = UserDto;
export type TenantAdminUserPageDto = PageUserDto;

export interface ListTenantUsersParams {
  readonly page: number;
  readonly size: number;
}

export interface SearchTenantUsersParams {
  readonly q: string;
  readonly page?: number;
  readonly size?: number;
}

/**
 * Wire DTO for the tenant-admin reach role catalogue (PTV2-1426). Same shape as the global
 * `/api/v1/admin/roles` catalogue (`RoleDTO`): camelCase, `id` IS the role guid (see
 * `RoleServiceImpl.toDto` — `id` is populated from the entity's `guid` column, not its numeric pk).
 */
export interface TenantRoleDto {
  readonly id?: string | null;
  readonly name: string;
  readonly description?: string | null;
  /** Server-owned provenance (PTV2-1442): built-in roles are non-deletable. */
  readonly builtIn?: boolean | null;
}

/**
 * A role's grammar permission grants (PTV2-1442): the `GET` response and the `PUT` replace-all body for
 * `/…/roles/{guid}/permissions`. Each entry is a five-segment
 * `entityGroup:entity:featureGroup:feature:action` string.
 */
export interface RolePermissionsDto {
  readonly permissions: readonly string[];
}

/** One allowed value of a permission-grammar slot (PTV2-1441), already camelCase on the wire. */
export interface PermissionTermDto {
  readonly value: string;
  readonly label: string;
  /** `scope` | `collection` for entityGroup terms; absent otherwise (and for the wildcard). */
  readonly group?: string | null;
}

export interface PermissionSlotDto {
  readonly name: string;
  readonly label: string;
  readonly values: readonly PermissionTermDto[];
}

export interface PermissionShapeDto {
  readonly id: string;
  readonly label: string;
  readonly template: string;
  readonly editableSlots: readonly string[];
}

/** The strongly-typed permission grammar vocabulary (PTV2-1441), driving the builder + reference. */
export interface PermissionVocabularyDto {
  readonly slots: readonly PermissionSlotDto[];
  readonly shapes: readonly PermissionShapeDto[];
}

export type ClusterGrantCapability = 'ADMINISTER' | 'OPERATE' | 'VIEW';

export interface ClusterGrantDto {
  readonly id: number;
  readonly global_user_guid: string;
  readonly tenant_uuid: string;
  readonly tenant_name?: string | null;
  readonly capability: ClusterGrantCapability;
  readonly granted_by?: string | null;
  readonly granted_at?: string | null;
  readonly revoked_at?: string | null;
}

export interface CreateClusterGrantRequestDto {
  readonly globalUserGuid: string;
  readonly tenantUuid: string;
  readonly capability: ClusterGrantCapability;
}

export interface ClusterGrantFilters {
  readonly globalUserGuid?: string;
  readonly tenantUuid?: string;
}

export interface TenantTemplateDto {
  readonly name: string;
  readonly description?: string | null;
  readonly is_default?: boolean;
  readonly created_by?: string | null;
  readonly created_at?: string | null;
  readonly definition?: unknown;
}

export interface CreateTenantTemplateRequestDto {
  readonly name: string;
  readonly description?: string | null;
  readonly definition?: unknown;
}

export interface ApplyTenantTemplateRequestDto {
  readonly templateName: string;
}

export const platformEndpoints = {
  listTenants: (
    page: number,
    size: number,
    options?: { signal?: AbortSignal },
  ): Promise<TenantPageDto> =>
    http.get<TenantPageDto>('/tenants', { ...options, query: { page, size } }),

  getTenant: (uuid: string, options?: { signal?: AbortSignal }): Promise<TenantDto> =>
    http.get<TenantDto>(`/tenants/${encodeURIComponent(uuid)}`, options),

  getTenantByName: (name: string, options?: { signal?: AbortSignal }): Promise<TenantDto> =>
    http.get<TenantDto>(`/tenants/by-name/${encodeURIComponent(name)}`, options),

  createTenant: (
    body: CreateTenantRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<TenantDto> => http.post<TenantDto>('/tenants', body, options),

  updateTenant: (
    uuid: string,
    body: UpdateTenantRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<TenantDto> =>
    http.put<TenantDto>(`/tenants/${encodeURIComponent(uuid)}`, body, options),

  initTenant: (uuid: string, options?: { signal?: AbortSignal }): Promise<JobLaunchDto> =>
    http.post<JobLaunchDto>(`/tenants/${encodeURIComponent(uuid)}/init`, undefined, options),

  // Seed is async (202 + job execution), env-gated; a disabled env returns an
  // empty-body 404 (`ResponseEntity.notFound().build()`), distinct from a
  // tenant-not-found 404 which carries the standard error envelope.
  seedTenant: (uuid: string, options?: { signal?: AbortSignal }): Promise<JobLaunchDto> =>
    http.post<JobLaunchDto>(`/tenants/${encodeURIComponent(uuid)}/seed`, undefined, options),

  getJob: (jobId: number, options?: { signal?: AbortSignal }): Promise<JobExecutionDto> =>
    http.get<JobExecutionDto>(`/jobs/${String(jobId)}`, options),

  /**
   * `GET /jobs` — returns the list of job executions visible to the caller.
   * The backend routes by tenant context: a global-plane user sees global/
   * cluster-orchestration jobs; a tenant user sees only their tenant's jobs.
   * The frontend does NOT branch on plane — it renders whatever the endpoint
   * returns. Accepts optional filter/pagination params per `JobController`.
   */
  listJobs: (
    params?: ListJobsParams,
    options?: { signal?: AbortSignal },
  ): Promise<JobExecutionDto[]> =>
    http.get<JobExecutionDto[]>('/jobs', {
      ...options,
      query: {
        ...(params?.start !== undefined ? { start: params.start } : {}),
        ...(params?.end !== undefined ? { end: params.end } : {}),
        ...(params?.status !== undefined ? { status: params.status } : {}),
        ...(params?.jobName !== undefined ? { jobName: params.jobName } : {}),
        ...(params?.page !== undefined ? { page: params.page } : {}),
        ...(params?.size !== undefined ? { size: params.size } : {}),
      },
    }),

  /**
   * `POST /jobs/{id}/restart` — restart a FAILED or STOPPED job. Returns the
   * new `JobExecutionDto` for the restarted execution (202 Accepted per the
   * backend `JobController`).
   */
  restartJob: (jobId: number, options?: { signal?: AbortSignal }): Promise<JobExecutionDto> =>
    http.post<JobExecutionDto>(`/jobs/${String(jobId)}/restart`, undefined, options),

  provisionTenantUser: (
    uuid: string,
    body: ProvisionTenantUserRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<ProvisionedUserDto> =>
    http.post<ProvisionedUserDto>(`/tenants/${encodeURIComponent(uuid)}/users`, body, options),

  replaceTenantUserRoles: (
    uuid: string,
    userGuid: string,
    body: ReplaceTenantUserRolesRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.put<undefined>(
      `/tenants/${encodeURIComponent(uuid)}/users/${encodeURIComponent(userGuid)}/roles`,
      body,
      { ...options, responseType: 'none' },
    ),

  /** `GET /tenants/{uuid}/users` — paged users inside the tenant (tenant-admin reach, PTV2-1426). */
  listTenantAdminUsers: (
    uuid: string,
    params: ListTenantUsersParams,
    options?: { signal?: AbortSignal },
  ): Promise<TenantAdminUserPageDto> =>
    http.get<TenantAdminUserPageDto>(`/tenants/${encodeURIComponent(uuid)}/users`, {
      ...options,
      query: { page: params.page, size: params.size },
    }),

  /** `GET /tenants/{uuid}/users/search?q=` — case-insensitive substring match, scoped to the tenant. */
  searchTenantUsers: (
    uuid: string,
    params: SearchTenantUsersParams,
    options?: { signal?: AbortSignal },
  ): Promise<TenantAdminUserPageDto> =>
    http.get<TenantAdminUserPageDto>(`/tenants/${encodeURIComponent(uuid)}/users/search`, {
      ...options,
      query: { q: params.q, page: params.page, size: params.size },
    }),

  /** `POST /tenants/{uuid}/users/{userGuid}/activate` — returns the reactivated user. */
  activateTenantUser: (
    uuid: string,
    userGuid: string,
    options?: { signal?: AbortSignal },
  ): Promise<TenantAdminUserDto> =>
    http.post<TenantAdminUserDto>(
      `/tenants/${encodeURIComponent(uuid)}/users/${encodeURIComponent(userGuid)}/activate`,
      undefined,
      options,
    ),

  /** `DELETE /tenants/{uuid}/users/{userGuid}` — deactivates the user inside the tenant. */
  deactivateTenantUser: (
    uuid: string,
    userGuid: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/tenants/${encodeURIComponent(uuid)}/users/${encodeURIComponent(userGuid)}`,
      { ...options, responseType: 'none' },
    ),

  /** `GET /tenants/{uuid}/roles?q=` — the tenant's own role catalogue (tenant-admin reach). */
  listTenantRoles: (
    uuid: string,
    q?: string,
    options?: { signal?: AbortSignal },
  ): Promise<TenantRoleDto[]> =>
    http.get<TenantRoleDto[]>(`/tenants/${encodeURIComponent(uuid)}/roles`, {
      ...options,
      query: { q: q !== undefined && q.length > 0 ? q : undefined },
    }),

  /** `GET /tenants/{uuid}/roles/{guid}` — a single role in the tenant's catalogue. */
  getTenantRole: (
    uuid: string,
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<TenantRoleDto> =>
    http.get<TenantRoleDto>(
      `/tenants/${encodeURIComponent(uuid)}/roles/${encodeURIComponent(guid)}`,
      options,
    ),

  createTenantRole: (
    uuid: string,
    body: TenantRoleDto,
    options?: { signal?: AbortSignal },
  ): Promise<TenantRoleDto> =>
    http.post<TenantRoleDto>(`/tenants/${encodeURIComponent(uuid)}/roles`, body, options),

  updateTenantRole: (
    uuid: string,
    guid: string,
    body: TenantRoleDto,
    options?: { signal?: AbortSignal },
  ): Promise<TenantRoleDto> =>
    http.put<TenantRoleDto>(
      `/tenants/${encodeURIComponent(uuid)}/roles/${encodeURIComponent(guid)}`,
      body,
      options,
    ),

  deleteTenantRole: (
    uuid: string,
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/tenants/${encodeURIComponent(uuid)}/roles/${encodeURIComponent(guid)}`,
      { ...options, responseType: 'none' },
    ),

  /** `GET /permissions/vocabulary` — the strongly-typed grammar vocabulary (PTV2-1441). */
  getPermissionVocabulary: (options?: { signal?: AbortSignal }): Promise<PermissionVocabularyDto> =>
    http.get<PermissionVocabularyDto>('/permissions/vocabulary', options),

  /** `GET /tenants/{uuid}/roles/{guid}/permissions` — a role's grammar grants (PTV2-1442). */
  getTenantRolePermissions: (
    uuid: string,
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<RolePermissionsDto> =>
    http.get<RolePermissionsDto>(
      `/tenants/${encodeURIComponent(uuid)}/roles/${encodeURIComponent(guid)}/permissions`,
      options,
    ),

  /** `PUT /tenants/{uuid}/roles/{guid}/permissions` — replace-all, escalation-guarded (PTV2-1442). */
  replaceTenantRolePermissions: (
    uuid: string,
    guid: string,
    body: RolePermissionsDto,
    options?: { signal?: AbortSignal },
  ): Promise<RolePermissionsDto> =>
    http.put<RolePermissionsDto>(
      `/tenants/${encodeURIComponent(uuid)}/roles/${encodeURIComponent(guid)}/permissions`,
      body,
      options,
    ),

  applyTenantTemplate: (
    uuid: string,
    body: ApplyTenantTemplateRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<JobLaunchDto> =>
    http.post<JobLaunchDto>(`/tenants/${encodeURIComponent(uuid)}/apply-template`, body, options),

  listTenantTemplates: (options?: { signal?: AbortSignal }): Promise<TenantTemplateDto[]> =>
    http.get<TenantTemplateDto[]>('/tenant-templates', options),

  createTenantTemplate: (
    body: CreateTenantTemplateRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<TenantTemplateDto> =>
    http.post<TenantTemplateDto>('/tenant-templates', body, options),

  listClusterGrants: (
    filters?: ClusterGrantFilters,
    options?: { signal?: AbortSignal },
  ): Promise<ClusterGrantDto[]> =>
    http.get<ClusterGrantDto[]>('/cluster-grants', {
      ...options,
      query: {
        global_user_guid: filters?.globalUserGuid,
        tenant_uuid: filters?.tenantUuid,
      },
    }),

  createClusterGrant: (
    body: CreateClusterGrantRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<ClusterGrantDto> =>
    http.post<ClusterGrantDto>('/cluster-grants', body, options),

  revokeClusterGrant: (id: number, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.delete<undefined>(`/cluster-grants/${String(id)}`, { ...options, responseType: 'none' }),

  // ── PTV2-1021 Phase 2: deployment-manager-backed override sub-actions ──
  //
  // The DM-health probe must NOT trigger the global 401→/login redirect on the
  // unauthenticated edge cases, and a 404/503 here is "feature off" not an
  // error — so the caller (`useDmHealth`) catches and maps. We still let 401
  // flow through the normal redirect (an unauthenticated platform user should
  // re-login regardless).
  getDeploymentManagerHealth: (options?: {
    signal?: AbortSignal;
  }): Promise<DeploymentManagerHealthDto> =>
    http.get<DeploymentManagerHealthDto>('/deployment-manager/health', options),

  // PTV2-1021 Phase 4: base image tags for the create-tenant override pre-fill.
  // camelCase on the wire (see BaseVersionsDto) — no snake_case mapping. A
  // 404/503/network error here means "DM unavailable" → no pre-fill, NOT an
  // error (handled in `useBaseVersions`).
  getBaseVersions: (options?: { signal?: AbortSignal }): Promise<BaseVersionsDto> =>
    http.get<BaseVersionsDto>('/deployment-manager/base-versions', options),

  // Override sub-actions: 202 + OverrideJobLaunchDto. Idempotent (a repeated
  // POST relaunches/resumes the job).
  deployTenant: (uuid: string, options?: { signal?: AbortSignal }): Promise<OverrideJobLaunchDto> =>
    http.post<OverrideJobLaunchDto>(
      `/tenants/${encodeURIComponent(uuid)}/deploy`,
      undefined,
      options,
    ),

  updateTenantDeployment: (
    uuid: string,
    options?: { signal?: AbortSignal },
  ): Promise<OverrideJobLaunchDto> =>
    http.post<OverrideJobLaunchDto>(
      `/tenants/${encodeURIComponent(uuid)}/update`,
      undefined,
      options,
    ),

  stopTenant: (uuid: string, options?: { signal?: AbortSignal }): Promise<OverrideJobLaunchDto> =>
    http.post<OverrideJobLaunchDto>(
      `/tenants/${encodeURIComponent(uuid)}/stop`,
      undefined,
      options,
    ),

  // DELETE /tenants/{uuid} → 202 + OverrideJobLaunchDto (tenant → wiping).
  // THIS DROPS THE TENANT SCHEMA. Gated to managePlatform + DM-reachable in UI.
  wipeTenant: (uuid: string, options?: { signal?: AbortSignal }): Promise<OverrideJobLaunchDto> =>
    http.delete<OverrideJobLaunchDto>(`/tenants/${encodeURIComponent(uuid)}`, options),

  getDockerhubCredentials: (options?: {
    signal?: AbortSignal;
  }): Promise<DockerHubCredentialsDto> =>
    http.get<DockerHubCredentialsDto>('/dockerhub-credentials', options),

  putDockerhubCredentials: (
    body: PutDockerHubCredentialsRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<DockerHubCredentialsDto> =>
    http.put<DockerHubCredentialsDto>('/dockerhub-credentials', body, options),

  /**
   * `GET /tenants/{uuid}/federated-identities` — a tenant's captured external identities by status
   * (default PENDING server-side) or by linked user (`userGuid`). The tenant-plane analogue of the
   * global `/admin/federated-identities` surface, on the tenant-admin reach path (PTV2-1472). Reuses the
   * global snake_case wire DTO — the shape is identical.
   */
  listTenantFederatedIdentities: (
    uuid: string,
    params: { status?: FederatedIdentityStatusDto; userGuid?: string },
    options?: { signal?: AbortSignal },
  ): Promise<FederatedIdentityDto[]> =>
    http.get<FederatedIdentityDto[]>(
      `/tenants/${encodeURIComponent(uuid)}/federated-identities`,
      {
        ...options,
        query: {
          ...(params.status !== undefined ? { status: params.status } : {}),
          ...(params.userGuid !== undefined ? { userGuid: params.userGuid } : {}),
        },
      },
    ),

  /** `POST /tenants/{uuid}/federated-identities/{guid}/link` — link a captured identity to a tenant user. */
  linkTenantFederatedIdentity: (
    uuid: string,
    guid: string,
    body: LinkFederatedIdentityRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<FederatedIdentityDto> =>
    http.post<FederatedIdentityDto>(
      `/tenants/${encodeURIComponent(uuid)}/federated-identities/${encodeURIComponent(guid)}/link`,
      body,
      options,
    ),

  /** `POST /tenants/{uuid}/federated-identities/{guid}/unlink` — deactivate (retire) an identity. 204. */
  unlinkTenantFederatedIdentity: (
    uuid: string,
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.post<undefined>(
      `/tenants/${encodeURIComponent(uuid)}/federated-identities/${encodeURIComponent(guid)}/unlink`,
      undefined,
      { ...options, responseType: 'none' },
    ),
};
